/**
 * The bonus moments.
 *
 * `VFX` is the only loud thing in this build: it binds to the game bus, turns
 * each outcome into one visually distinct burst, floats the numbers that
 * explain it, kicks the post-processing bloom, and shakes the table. Everything
 * it owns is pooled and self-recycling — a session fires these hundreds of
 * times, so they have to be sharp, quick, and then completely gone.
 *
 * Wiring:
 *
 * ```ts
 * const vfx = new VFX({ onBloom: (boost) => postfx.setBloomBoost(boost) });
 * layers.vfx.addChild(vfx.view);
 * const unbind = vfx.bind(bus);
 * // in the render loop, before rendering:
 * vfx.update(dt);
 * ```
 *
 * The bloom kick is deliberately not a filter reference. `VFX` owns a single
 * scalar, eases it back to zero itself, and publishes it two ways — the
 * {@link VFXOptions.onBloom} callback and the {@link VFX.bloomBoost} getter —
 * so `postfx.ts` can either subscribe or poll and neither module has to import
 * the other. Screen shake works the same way.
 *
 * Reduced motion is honoured properly rather than nominally: particle density
 * drops to roughly a quarter, bloom cores and lingering trails are suppressed
 * entirely, popups lose their overshoot, and the bloom kick and screen shake
 * are hard-disabled — no flashes, no camera movement.
 */

import { Container } from 'pixi.js';

import { bus as sharedBus, type EventBus } from '@/core/bus';
import { CARD, DESIGN, PILES, pilePosition } from '@/pixi/layout';
import { POINTS, PILE_COUNT, type Resolution } from '@/game/types';

import { ParticleSystem, type ParticleSystemOptions, type SparkShape } from '@/vfx/particles';
import { StreakAura, StreakTracker, type StreakChange } from '@/vfx/streakAura';
import { LADDER_MAX, momentum } from '@/vfx/streakLadder';
import { StreakBadge } from '@/vfx/streakBadge';
import { LevelBanner } from '@/vfx/levelBanner';
import { QUEUE_GAP, levelPlan } from '@/vfx/levelCeremony';
import {
  ScorePopupLayer,
  type PopupEntry,
  type PopupRequest,
  type PopupVariant,
  type ScorePopupOptions,
} from '@/vfx/scorePopup';

export { ParticleSystem, ScorePopupLayer };
export type {
  ParticleSystemOptions,
  PopupEntry,
  PopupRequest,
  PopupVariant,
  ScorePopupOptions,
  SparkShape,
};

/* ------------------------------------------------------------------ tuning -- */

/** How hard each outcome kicks the bloom, 0..1. Additive, then clamped. */
const BLOOM_KICK = {
  twentyOne: 0.34,
  fiveCard: 0.48,
  wild: 0.62,
  /** Base kick for a streak; the level adds to it. */
  streak: 0.14,
  streakPerLevel: 0.09,
  /**
   * A level-up's share of the frame.
   *
   * Under a plain 21 on purpose. A level is a milestone, not a score, and it
   * arrives nine or ten times a round against a 21's two or three — the loudest
   * thing on screen must not be the thing that happens most and pays least.
   * The milestone multiplier in `levelPlan` is what lifts every fifth one.
   */
  level: 0.26,
  perfectGame: 1,
} as const;

/** Impulse energy per outcome, 0..1. */
const SHAKE_KICK = {
  twentyOne: 0.2,
  fiveCard: 0.3,
  wild: 0.34,
  bust: 0.55,
  streak: 0.1,
  streakPerLevel: 0.08,
  perfectGame: 0.8,
} as const;

/**
 * Camera punch per outcome: how far the table leans in, as a fraction above 1.
 *
 * Only the rewards that are supposed to feel like impacts get one. A bust does
 * not: it already owns the hardest hit-stop and the shake, and a mistake that
 * punched the camera would be borrowing the grammar of a reward.
 */
const PUNCH_KICK = {
  twentyOne: 0.014,
  fiveCard: 0.020,
  wild: 0.025,
  /**
   * Base for a streak; the rung adds to it. 2x lands at 2.5%, 6x at 6.0%.
   *
   * The first pass at this was 0.8%-2.4% and the verdict was "seems the same",
   * which was fair — a camera lean under 3% is a thing you can only see in a
   * side-by-side. It was pitched there to keep the table clear of the letterbox
   * on a wide viewport, but that caution had the priorities backwards: the bars
   * exist precisely to cover what the table pushes past the design rect, they
   * already cover a shake doing the same thing, and 6% for a sixth of a second
   * is a camera slam rather than a crop anyone can read as one.
   */
  streak: 0.010,
  streakPerLevel: 0.010,
  perfectGame: 0.075,
} as const;

/** Seconds for the bloom boost to fall to 1/e. Short enough to read as a flash. */
const BLOOM_DECAY = 0.42;
/**
 * Seconds for the punch to fall to 1/e.
 *
 * Faster than the bloom and slower than the shake. The punch is the weight of
 * the hit, so it has to outlast the flash that announces it and settle before
 * the callout has finished being read — anything longer and the table appears
 * to be drifting rather than recovering.
 */
const PUNCH_DECAY = 0.13;
/** Seconds for shake energy to fall to 1/e. Impact, not vibration. */
const SHAKE_DECAY = 0.17;
/** Below this both signals snap to zero so subscribers get a clean rest state. */
const EPSILON = 0.002;

/** Peak shake displacement, in design units, at full energy. */
const SHAKE_AMPLITUDE_X = CARD.width * 0.085;
const SHAKE_AMPLITUDE_Y = CARD.width * 0.062;
const SHAKE_AMPLITUDE_ROT = 0.011;
/** Incommensurate frequencies, so the decay reads as an impact rather than a buzz. */
const SHAKE_FREQ_X = 61;
const SHAKE_FREQ_Y = 47;
const SHAKE_FREQ_ROT = 39;

/**
 * Fixed order for a stacked play, matching the audio module's arpeggio: the
 * rescue, then the score, then the payout, then the ladder. Sight and sound
 * have to agree or a four-bonus play turns to mush in both.
 */
const BONUS_ORDER = ['wild', 'twentyOne', 'fiveCard'] as const;

/** Gap between bursts inside one play, so four outcomes read as four events. */
const BURST_STAGGER = 0.09;

/** How far above the pile the numbers start. */
const POPUP_OFFSET_Y = -CARD.height * 0.5;

const MAX_STEP = 1 / 15;

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

function prefersReducedMotion(): boolean {
  const media = (globalThis as { matchMedia?: (query: string) => MediaQueryList }).matchMedia;
  if (typeof media !== 'function') return false;
  try {
    return media.call(globalThis, '(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/* ----------------------------------------------------------------- queue -- */

/** Rendered frames the warm-up is held for. See {@link VFX.warm}. */
const WARM_FRAMES = 3;

type EffectKind = 'twentyOne' | 'fiveCard' | 'wild' | 'bust' | 'streak' | 'perfectGame';

/**
 * A scheduled burst. Preallocated and reused so staggering a stacked play costs
 * no allocation and leaves no timers to cancel on teardown.
 */
interface Scheduled {
  active: boolean;
  at: number;
  kind: EffectKind;
  x: number;
  y: number;
  level: number;
}

const QUEUE_SIZE = 24;

/* ------------------------------------------------------------------- vfx -- */

export interface VFXOptions {
  /** Hard particle ceiling. Default 1800. */
  capacity?: number;
  /** Global particle density multiplier, 1 = as designed. */
  intensity?: number;
  /** Maximum simultaneous score popups. Default 28. */
  popupCapacity?: number;
  /**
   * Force reduced motion on or off. Omitted, it follows
   * `prefers-reduced-motion` and keeps following it if the user changes it
   * mid-session.
   */
  reducedMotion?: boolean;
  /** Called whenever the bloom boost changes, including the final drop to 0. */
  onBloom?: (boost: number) => void;
  /** Called whenever the shake offset changes, including the final drop to 0. */
  onShake?: (x: number, y: number, rotation: number) => void;
  /** Called whenever the camera punch changes, as a scale multiplier (1 at rest). */
  onPunch?: (scale: number) => void;
}

export interface VFXBindOptions {
  /** Fire bursts and popups from `pile:resolve`. Default true. */
  resolutions?: boolean;
  /** Fire the full-table celebration on a perfect game. Default true. */
  finale?: boolean;
  /** Clear everything on `game:start`. Default true. */
  reset?: boolean;
}

export class VFX {
  /** Add this to the stage's vfx layer, above the cards. */
  readonly view: Container;
  /**
   * The screen-space streak effects: the persistent rim and the badge callout.
   *
   * Parent this to the **overlay** layer, not to {@link view} — see the note
   * where it is constructed.
   */
  readonly overlay: Container;
  /** The persistent rim that says a run is alive. Inside {@link overlay}. */
  readonly aura: StreakAura;
  /** The multiplier callout. Inside {@link overlay}. */
  readonly badge: StreakBadge;
  /** The level-up banner — the callout's cool sibling. Inside {@link overlay}. */
  readonly levelBanner: LevelBanner;

  readonly particles: ParticleSystem;
  readonly popups: ScorePopupLayer;

  readonly #queue: Scheduled[] = [];

  #bloom = 0;
  #punch = 0;
  #shakeEnergy = 0;
  /** Classifies streak changes: opened, lost, taken back, or never opened. */
  readonly #streak = new StreakTracker();
  /** Frames left of the invisible first-render warm-up. See {@link warm}. */
  #warmFrames = 0;
  #shakeTime = 0;
  #shakeX = 0;
  #shakeY = 0;
  #shakeRot = 0;

  #reduced: boolean;
  #onBloom: ((boost: number) => void) | undefined;
  #onPunch: ((scale: number) => void) | undefined;
  #onShake: ((x: number, y: number, rotation: number) => void) | undefined;

  #motionQuery: MediaQueryList | null = null;
  #destroyed = false;

  constructor(options: VFXOptions = {}) {
    this.#reduced = options.reducedMotion ?? prefersReducedMotion();
    this.#onBloom = options.onBloom;
    this.#onShake = options.onShake;
    this.#onPunch = options.onPunch;

    this.particles = new ParticleSystem({
      capacity: options.capacity,
      intensity: options.intensity,
      reducedMotion: this.#reduced,
    });
    this.popups = new ScorePopupLayer({
      capacity: options.popupCapacity,
      reducedMotion: this.#reduced,
    });

    this.view = new Container({ label: 'vfx' });
    // Numbers always sit above the burst that explains them.
    this.view.addChild(this.particles.view, this.popups.view);

    // Deliberately NOT children of `view`. `view` goes on the `vfx` layer,
    // which parallaxes with the play plane at ratio 1; both of these belong on
    // `overlay`, whose ratio is exactly 0. The rim is the frame around the
    // scene and the badge is a callout over it — neither is an object in it,
    // and a rim of light that slides under the pointer reads as a prop lying on
    // the table. `main.ts` is responsible for parenting `overlay`.
    this.aura = new StreakAura();
    this.badge = new StreakBadge({ reducedMotion: this.#reduced });
    this.levelBanner = new LevelBanner({ reducedMotion: this.#reduced });
    this.overlay = new Container({ label: 'vfx-overlay' });
    this.overlay.addChild(this.aura, this.badge, this.levelBanner);

    for (let i = 0; i < QUEUE_SIZE; i++) {
      this.#queue.push({ active: false, at: 0, kind: 'twentyOne', x: 0, y: 0, level: 0 });
    }

    if (options.reducedMotion === undefined) this.#watchMotionPreference();
  }

  #watchMotionPreference(): void {
    const media = (globalThis as { matchMedia?: (query: string) => MediaQueryList }).matchMedia;
    if (typeof media !== 'function') return;
    try {
      const query = media.call(globalThis, '(prefers-reduced-motion: reduce)');
      query.addEventListener('change', this.#onMotionPreferenceChange);
      this.#motionQuery = query;
    } catch {
      /* older engines without matchMedia listeners simply keep the initial value */
    }
  }

  #onMotionPreferenceChange = (event: MediaQueryListEvent): void => {
    this.setReducedMotion(event.matches);
  };

  /* --------------------------------------------------------------- config -- */

  get reducedMotion(): boolean {
    return this.#reduced;
  }

  setReducedMotion(value: boolean): void {
    if (this.#reduced === value) return;
    this.#reduced = value;
    this.particles.setReducedMotion(value);
    this.popups.setReducedMotion(value);
    this.badge.setReducedMotion(value);
    this.levelBanner.setReducedMotion(value);
    if (value) {
      this.#bloom = 0;
      this.#shakeEnergy = 0;
      this.#publishBloom();
      this.#publishShake(0, 0, 0);
    }
  }

  setIntensity(value: number): void {
    this.particles.setIntensity(value);
  }

  /**
   * Pin the score popups' rasterisation scale to true device pixels. Pass
   * `renderer.resolution * stageScale` from the resize handler, or `null` to go
   * back to deriving it automatically from the layer's world transform.
   */
  setRenderScale(scale: number | null): void {
    this.popups.setRenderScale(scale);
  }

  /* ----------------------------------------------------------- post hooks -- */

  /**
   * Current additive bloom boost, 0..1, easing back to 0 on its own. `postfx`
   * can poll this every frame instead of subscribing to
   * {@link VFXOptions.onBloom}; both are driven from the same scalar.
   */
  get bloomBoost(): number {
    return this.#bloom;
  }

  /** Current screen-shake offset in design units. Add to the stage's base position. */
  get shakeX(): number {
    return this.#shakeX;
  }

  get shakeY(): number {
    return this.#shakeY;
  }

  /** Current screen-shake roll, in radians. */
  get shakeRotation(): number {
    return this.#shakeRot;
  }

  /** Push the bloom up by `amount` (0..1). Suppressed entirely under reduced motion. */
  pumpBloom(amount: number): void {
    if (this.#reduced || amount <= 0) return;
    this.#bloom = clamp(this.#bloom + amount, 0, 1);
    this.#publishBloom();
  }

  /** Current camera punch as a fraction above 1, easing back to 0 on its own. */
  get punch(): number {
    return this.#punch;
  }

  /**
   * Lean the camera in by `amount` (a fraction above 1), then let it settle.
   *
   * Clamped well below the stage's own ceiling: this is weight, not a zoom.
   */
  pumpPunch(amount: number): void {
    if (this.#reduced || amount <= 0) return;
    this.#punch = clamp(this.#punch + amount, 0, 0.09);
    this.#publishPunch();
  }

  /** Add an impact impulse. `strength` is 0..1. Suppressed under reduced motion. */
  shake(strength: number): void {
    if (this.#reduced || strength <= 0) return;
    this.#shakeEnergy = clamp(this.#shakeEnergy + strength, 0, 1);
  }

  /* ---------------------------------------------------------------- ticks -- */

  /** Advance particles, popups, the burst queue and the post signals. Seconds. */
  /**
   * Re-fit whatever was sized to the design rect after an orientation change.
   *
   * Only the aura needs it: bursts, popups and the badge all derive their
   * positions at the moment they are fired, so they pick up the new layout for
   * free. The aura is a persistent quad the size of the table, and does not.
   */
  relayout(): void {
    this.aura.relayout();
  }

  /**
   * Draw the streak effects once, invisibly, before the player can earn one.
   *
   * Everything the first streak needs — the aura's shader program, the badge's
   * text textures, its gradients and its blur filters — is created lazily, on
   * the frame it is first rendered. Left alone, that frame is the first streak
   * itself: the single most emphatic moment in the game, and the one that can
   * least afford a compile and an upload on the main thread. It is why the
   * first streak stutters and later ones do not.
   *
   * The same is true of the popups and the particles: a `Text` is not
   * rasterised and a `ParticleContainer` allocates nothing until each is
   * actually drawn, so the first `+400` and the first burst pay for themselves
   * on the frame the game has just made loudest.
   *
   * A few frames rather than one, because a filtered object goes through a
   * render target and the filter is not built until that target is drawn.
   */
  warm(): void {
    if (this.#destroyed) return;
    this.aura.warm();
    this.badge.warm();
    this.popups.warm();
    this.particles.warm();
    this.#warmFrames = WARM_FRAMES;
  }

  update(dt: number): void {
    if (this.#destroyed) return;
    const step = clamp(dt, 0, MAX_STEP);

    if (this.#warmFrames > 0 && --this.#warmFrames === 0) {
      this.aura.cool();
      this.badge.cool();
      this.popups.cool();
      this.particles.cool();
    }

    if (step > 0) {
      for (const slot of this.#queue) {
        if (!slot.active) continue;
        slot.at -= step;
        if (slot.at <= 0) {
          slot.active = false;
          this.#fire(slot.kind, slot.x, slot.y, slot.level);
        }
      }
    }

    this.particles.update(step);
    this.popups.update(step);
    // Runs on every frame, not only while a streak is alive: the aura owns its
    // own fade-out and break drain, and both have to keep ticking after the
    // level has already gone to zero.
    this.aura.update(step);

    if (step > 0) this.#decay(step);
  }

  #decay(dt: number): void {
    if (this.#bloom > 0) {
      const next = this.#bloom * Math.exp(-dt / BLOOM_DECAY);
      this.#bloom = next < EPSILON ? 0 : next;
      this.#publishBloom();
    }

    if (this.#punch > 0) {
      const next = this.#punch * Math.exp(-dt / PUNCH_DECAY);
      this.#punch = next < EPSILON ? 0 : next;
      this.#publishPunch();
    }

    if (this.#shakeEnergy > 0 || this.#shakeX !== 0 || this.#shakeY !== 0 || this.#shakeRot !== 0) {
      this.#shakeTime += dt;
      const next = this.#shakeEnergy * Math.exp(-dt / SHAKE_DECAY);
      this.#shakeEnergy = next < EPSILON ? 0 : next;

      const e = this.#shakeEnergy;
      const x = e === 0 ? 0 : e * SHAKE_AMPLITUDE_X * Math.sin(this.#shakeTime * SHAKE_FREQ_X);
      const y = e === 0 ? 0 : e * SHAKE_AMPLITUDE_Y * Math.sin(this.#shakeTime * SHAKE_FREQ_Y + 1.7);
      const rot = e === 0 ? 0 : e * SHAKE_AMPLITUDE_ROT * Math.sin(this.#shakeTime * SHAKE_FREQ_ROT + 0.6);
      this.#publishShake(x, y, rot);
    }
  }

  #publishBloom(): void {
    this.#onBloom?.(this.#bloom);
  }

  #publishPunch(): void {
    this.#onPunch?.(1 + this.#punch);
  }

  #publishShake(x: number, y: number, rotation: number): void {
    this.#shakeX = x;
    this.#shakeY = y;
    this.#shakeRot = rotation;
    this.#onShake?.(x, y, rotation);
  }

  /* -------------------------------------------------------------- effects -- */

  /** Mint. Crisp, precise, snapping outward. */
  twentyOne(x: number, y: number): void {
    this.#fire('twentyOne', x, y, 0);
  }

  /** Gold. Richer and more generous — the biggest per-pile payout. */
  fiveCard(x: number, y: number): void {
    this.#fire('fiveCard', x, y, 0);
  }

  /** Violet. The rescue card, so the showiest of the four. */
  wild(x: number, y: number): void {
    this.#fire('wild', x, y, 0);
  }

  /** Rose. Short, sharp, unhappy — dust and a downward drift. */
  bust(x: number, y: number): void {
    this.#fire('bust', x, y, 0);
  }

  /** Escalating, `level` 1..5 for the 2x..6x callouts. */
  streak(x: number, y: number, level: number): void {
    this.#fire('streak', x, y, level);
  }

  /** The full-table celebration. Rare, so it takes the whole frame. */
  perfectGame(): void {
    this.#fire('perfectGame', 0, 0, 0);
  }

  #fire(kind: EffectKind, x: number, y: number, level: number): void {
    if (this.#destroyed) return;
    switch (kind) {
      case 'twentyOne':
        this.particles.twentyOne(x, y);
        this.pumpBloom(BLOOM_KICK.twentyOne);
        this.shake(SHAKE_KICK.twentyOne);
        this.pumpPunch(PUNCH_KICK.twentyOne);
        break;
      case 'fiveCard':
        this.particles.fiveCard(x, y);
        this.pumpBloom(BLOOM_KICK.fiveCard);
        this.shake(SHAKE_KICK.fiveCard);
        this.pumpPunch(PUNCH_KICK.fiveCard);
        break;
      case 'wild':
        this.particles.wild(x, y);
        this.pumpBloom(BLOOM_KICK.wild);
        this.shake(SHAKE_KICK.wild);
        this.pumpPunch(PUNCH_KICK.wild);
        break;
      case 'bust':
        // No bloom: a bust is the one moment that must never look like a reward.
        this.particles.bust(x, y);
        this.shake(SHAKE_KICK.bust);
        break;
      case 'streak': {
        const l = clamp(Math.round(level), 1, LADDER_MAX);
        // Bloom and shake ride the same front-loaded curve as the badge and the
        // aura. Scaling them linearly in the level spends the loudest screen
        // response on 5x and 6x, which need two wilds on top of a running 4x
        // and are effectively unreachable. Peak is unchanged; it just arrives
        // at the rungs a player actually sees. See @/vfx/streakLadder.
        const drive = momentum(l);
        this.particles.streak(x, y, l);
        this.pumpBloom(BLOOM_KICK.streak + BLOOM_KICK.streakPerLevel * LADDER_MAX * drive);
        this.shake(SHAKE_KICK.streak + SHAKE_KICK.streakPerLevel * LADDER_MAX * drive);
        this.pumpPunch(PUNCH_KICK.streak + PUNCH_KICK.streakPerLevel * LADDER_MAX * drive);
        break;
      }
      case 'perfectGame':
        this.particles.perfectGame();
        this.pumpBloom(BLOOM_KICK.perfectGame);
        this.shake(SHAKE_KICK.perfectGame);
        this.pumpPunch(PUNCH_KICK.perfectGame);
        break;
    }
  }

  /**
   * A level was reached.
   *
   * The whole ceremony from one call: banner, motes, bloom, and — on a
   * milestone — a second, brighter beat. Deliberately does NOT shake, punch or
   * move the scene in any way; see the note at the top of `levelBanner.ts`.
   *
   * @param level - the level reached.
   * @param x, y - where the pill is, in design units: the motes are pushed away
   *   from it, so the light reads as coming from the thing that just changed.
   * @param streakOnScreen - true when a streak callout is already playing from
   *   the same move. The banner then waits for it rather than landing on top:
   *   one emphasised thing at a time.
   */
  levelUp(level: number, x: number, y: number, streakOnScreen = false): void {
    const plan = levelPlan(level);
    const delay = streakOnScreen ? QUEUE_GAP : 0;

    this.levelBanner.show(plan.level, delay);

    if (this.#reduced) return;

    // On the banner's leading edge rather than with it: the light arrives just
    // after the letters begin, so the banner reads as the cause.
    const impulse = delay + 0.045;
    window.setTimeout(
      () => {
        if (this.#destroyed) return;
        this.particles.levelUp(x, y, plan.moteSpeed);
        // A bloom lift is the "filled cool pulse" — the rim and the whole scene
        // brighten together, additively, over whatever the streak glow is
        // already doing. It never recolours the streak's own light.
        this.pumpBloom(BLOOM_KICK.level * plan.rimLift);
        if (plan.rimPulses > 1) {
          window.setTimeout(() => {
            if (this.#destroyed) return;
            this.pumpBloom(BLOOM_KICK.level * plan.rimLift * 0.8);
          }, plan.rimGap * 1000);
        }
      },
      impulse * 1000,
    );
  }

  /** Queue a burst `delay` seconds from now. Fires immediately if the queue is full. */
  #schedule(kind: EffectKind, x: number, y: number, level: number, delay: number): void {
    if (delay <= 0) {
      this.#fire(kind, x, y, level);
      return;
    }
    for (const slot of this.#queue) {
      if (slot.active) continue;
      slot.active = true;
      slot.at = delay;
      slot.kind = kind;
      slot.x = x;
      slot.y = y;
      slot.level = level;
      return;
    }
    this.#fire(kind, x, y, level);
  }

  /* ------------------------------------------------------------- gameplay -- */

  /**
   * The whole visual response to one play, derived from the rules engine's
   * `Resolution`: the bursts in the same order the audio arpeggiates them, each
   * a beat behind the last, and the numbers stacked above the pile.
   */
  resolve(resolution: Resolution): void {
    if (this.#destroyed) return;

    const pile = clamp(Math.round(resolution.pile), 0, PILE_COUNT - 1);
    const base = pilePosition(pile);
    // Aim at the middle of the fan rather than the top card, so a five-card
    // pile bursts from its own centre of mass.
    const x = base.x;
    const y = base.y + (Math.max(1, resolution.cards.length) - 1) * PILES.fan * 0.5;
    const popupY = y + POPUP_OFFSET_Y;

    if (resolution.isBust) {
      this.#fire('bust', x, y, 0);
      this.popups.show({ text: 'BUST', variant: 'bust', x, y: y + CARD.height * 0.1 });
      this.#syncStreak(0);
      return;
    }

    const entries: PopupEntry[] = [];
    let beat = 0;

    for (const kind of BONUS_ORDER) {
      if (!resolution.bonuses.includes(kind)) continue;
      this.#schedule(kind, x, y, 0, beat * BURST_STAGGER);
      entries.push({ text: `+${POINTS[kind]}`, variant: kind });
      beat++;
    }

    if (resolution.streakLevel > 0) {
      const level = clamp(resolution.streakLevel, 1, 5);
      this.#schedule('streak', x, y, level, beat * BURST_STAGGER);
      // The badge lands a beat *after* the pile burst it belongs to. Firing
      // them together would put the callout and its own trigger on the same
      // frame, which is what made a streak read as "one more burst at the
      // pile" rather than as its own event.
      this.badge.show(level, resolution.streakPoints, (beat + 1) * BURST_STAGGER);
      const label = resolution.streakLabel ?? '';
      entries.push({
        text: label ? `${label}  +${resolution.streakPoints}` : `+${resolution.streakPoints}`,
        variant: 'streak',
      });
      beat++;
    }

    this.#syncStreak(resolution.streakLevel);

    if (entries.length > 0) this.popups.showStack(entries, x, popupY);
  }

  /**
   * Drive the aura from the level the rules engine reports.
   *
   * The distinction that matters is between a streak *ending* and a streak
   * *never having opened*. `streakLevel` is 0 in both cases — the first scoring
   * play of a round has no streak yet — so the previous level is what separates
   * them. Only a drop from above zero is a break, and only a break gets the
   * white blow-out.
   */
  #syncStreak(level: number): void {
    this.#applyStreak(this.#streak.resolve(level), this.#streak.level);
  }

  /**
   * Put the streak back where a snapshot says it is — an undo.
   *
   * Deliberately **not** {@link resolve}'s path. An undo is not a streak
   * ending; it is a play that never happened. {@link StreakTracker.restore}
   * can never return `break`, so taking back the play that lost a run cannot
   * flash "you lost it" at a player who is in the middle of un-losing it.
   *
   * Restoring the tracker's level also matters for the play *after* the undo:
   * left at a stale zero, a continuing run would be misread as a new one.
   */
  restoreStreak(level: number): void {
    this.#applyStreak(this.#streak.restore(level), this.#streak.level);
  }

  #applyStreak(change: StreakChange, level: number): void {
    switch (change) {
      case 'open':
        this.aura.setLevel(level);
        break;
      case 'break':
        this.aura.breakStreak();
        break;
      case 'release':
        this.aura.setLevel(0);
        break;
      case 'idle':
        break;
    }
  }

  /** The end-of-game finale. Only worth calling when the player actually cleared the table. */
  celebratePerfectGame(): void {
    this.perfectGame();
    this.popups.show({
      text: `PERFECT  +${POINTS.perfectGame}`,
      variant: 'grand',
      x: DESIGN.width / 2,
      y: DESIGN.height * 0.42,
      delay: 0.22,
    });
  }

  /* ------------------------------------------------------------------ bus -- */

  /**
   * Subscribe to the game bus. Returns an unsubscribe function.
   *
   * Only `pile:resolve` is used for outcomes — it already carries `isBust`, and
   * the engine emits a separate `bust` event for the same play, so listening to
   * both would double-fire.
   */
  bind(source: EventBus = sharedBus, options: VFXBindOptions = {}): () => void {
    const withResolutions = options.resolutions ?? true;
    const withFinale = options.finale ?? true;
    const withReset = options.reset ?? true;
    const offs: Array<() => void> = [];

    if (withReset) {
      offs.push(source.on('game:start', () => this.clear()));
    }

    if (withResolutions) {
      offs.push(source.on('pile:resolve', (event) => this.resolve(event.resolution)));
      offs.push(
        source.on('level:up', (event) => {
          // The pill sits under the score, at the top of the board. Motes are
          // pushed away from there so the light comes from the thing that
          // changed — but everything born above the play area is otherwise
          // banned from this effect: hard geometry launched from a HUD element
          // crosses the cards and reads as hairlines over them.
          const x = DESIGN.width * 0.5;
          const y = DESIGN.height * 0.12;
          // `streakLevel` is non-zero exactly when a callout is on screen from
          // this same move, which is the case the queue gap exists for.
          this.levelUp(event.level, x, y, event.snapshot.streakLevel > 0);
        }),
      );
    }

    // An undone play's numbers are no longer true, so they go immediately. The
    // particles are left to die on their own — killing a burst mid-flight reads
    // as a dropped frame, not as an undo.
    //
    // The aura has to be put back too. It is the one piece of streak state with
    // no other route home: the HUD pill and the pile totals both re-sync from
    // the snapshot on this event, but the aura is driven only from `resolve()`,
    // which an undo never calls. Without this, undoing the play that broke a
    // streak leaves the rim dark while the HUD shows the streak restored.
    offs.push(
      source.on('undo', (event) => {
        this.popups.clear();
        this.restoreStreak(event.snapshot.streakLevel);
      }),
    );

    if (withFinale) {
      offs.push(
        source.on('game:over', (event) => {
          if (event.final.perfectGame) this.celebratePerfectGame();
        }),
      );
    }

    return () => {
      for (const off of offs) off();
      offs.length = 0;
    };
  }

  /* --------------------------------------------------------------- teardown -- */

  /** Kill everything on screen and reset the post signals. */
  clear(): void {
    for (const slot of this.#queue) slot.active = false;
    this.particles.clear();
    this.popups.clear();
    this.#streak.reset();
    this.aura.reset();
    this.badge.clear();
    this.levelBanner.clear();
    this.#bloom = 0;
    this.#shakeEnergy = 0;
    this.#publishBloom();
    this.#publishShake(0, 0, 0);
  }

  dispose(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.#motionQuery?.removeEventListener('change', this.#onMotionPreferenceChange);
    this.#motionQuery = null;
    this.#onBloom = undefined;
    this.#onPunch = undefined;
    this.#onShake = undefined;
    for (const slot of this.#queue) slot.active = false;
    this.view.removeChildren();
    this.particles.dispose();
    this.popups.dispose();
    this.view.destroy();
    // Parented by main.ts to a different layer, so it has to be detached here
    // rather than falling out with `view`.
    this.overlay.removeFromParent();
    this.overlay.destroy({ children: true });
  }
}
